import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Typography,
  InputAdornment,
  TextField,
  Button,
  Box,
  Avatar,
  AvatarGroup,
  Chip,
} from '@mui/material';
import { Search, PersonAdd, Groups } from '@mui/icons-material';
import { Areas } from '../GamifyIcon';
import { Area } from './InteractiveMap.types';
import {
  AreaListContainer,
  AreaListHeader,
  AreaSearchField,
  AreaList,
  AreaListItemContainer,
  AreaListItemText,
} from './InteractiveMap.styles';

interface AreaListProps {
  areas: Area[];
  selectedAreaId?: string;
  onAreaSelect: (area: Area) => void;
  editingAreaId?: string;
  isEditMode?: boolean;
  editingAreaName?: string;
  onAreaNameChange?: (name: string) => void;
  onAreaAssignmentClick?: (area: Area) => void;
}

const AreaListComponent: React.FC<AreaListProps> = ({
  areas,
  selectedAreaId,
  onAreaSelect,
  editingAreaId,
  isEditMode,
  editingAreaName,
  onAreaNameChange,
  onAreaAssignmentClick,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const listRef = useRef<HTMLUListElement>(null);
  const selectedItemRef = useRef<HTMLLIElement>(null);

  // Auto-scroll to selected area when it changes
  useEffect(() => {
    if (selectedAreaId && selectedItemRef.current && listRef.current) {
      // Small delay to ensure the DOM is updated
      setTimeout(() => {
        selectedItemRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
    }
  }, [selectedAreaId]);

  const filteredAreas = useMemo(() => {
    if (!searchTerm.trim()) return areas;

    const searchLower = searchTerm.toLowerCase();
    return areas.filter((area) =>
      area.name.toLowerCase().includes(searchLower)
    );
  }, [areas, searchTerm]);

  const handleAreaClick = (area: Area) => {
    onAreaSelect(area);
  };

  const handleAssignmentClick = (e: React.MouseEvent, area: Area) => {
    e.stopPropagation();
    onAreaAssignmentClick?.(area);
  };

  const formatAreaDescription = (area: Area) => {
    const parts = [];
    if (area.description) {
      parts.push(area.description);
    }
    if (area.coords && area.coords.length > 0) {
      parts.push(`${area.coords.length} points`);
    }
    return parts.join(' • ');
  };

  const renderAreaAssignments = (area: Area) => {
    const totalAssigned =
      (area.users?.length || 0) + (area.circles?.length || 0);

    if (totalAssigned === 0) {
      return (
        <Box sx={{ mt: 1 }}>
          <Chip
            size="small"
            label="No assignments"
            sx={{
              fontSize: '0.75rem',
              height: '20px',
              backgroundColor: (theme) => theme.custom.background2,
              color: (theme) => theme.custom.textLight,
              border: (theme) => `1px solid ${theme.custom.stroke}`,
            }}
          />
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {area.users && area.users.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AvatarGroup
              max={3}
              sx={{
                '& .MuiAvatar-root': {
                  width: 20,
                  height: 20,
                  fontSize: '0.75rem',
                },
              }}
            >
              {area.users.map((user) => (
                <Avatar
                  key={user.id}
                  src={user.avatarFile}
                  alt={user.name}
                  sx={{ width: 20, height: 20, fontSize: '0.75rem' }}
                >
                  {user.name?.[0]?.toUpperCase()}
                </Avatar>
              ))}
            </AvatarGroup>
            <Typography
              variant="caption"
              sx={{ color: (theme) => theme.custom.textLight }}
            >
              {area.users.length} user{area.users.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}

        {area.circles && area.circles.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Groups
              sx={{
                fontSize: 16,
                color: (theme) => theme.custom.textLight,
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: (theme) => theme.custom.textLight }}
            >
              {area.circles.length} team{area.circles.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <AreaListContainer>
      <AreaListHeader>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ color: (theme) => theme.custom.text }}
        >
          Areas ({areas.length})
        </Typography>
        <AreaSearchField
          fullWidth
          size="small"
          placeholder="Search areas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </AreaListHeader>

      <AreaList ref={listRef}>
        {filteredAreas.length === 0 ? (
          <AreaListItemContainer>
            <AreaListItemText
              primary={searchTerm ? 'No areas found' : 'No areas available'}
              secondary={
                searchTerm
                  ? 'Try adjusting your search terms'
                  : 'Create your first area to get started'
              }
            />
          </AreaListItemContainer>
        ) : (
          filteredAreas.map((area) => (
            <AreaListItemContainer
              key={area.id}
              ref={selectedAreaId === area.id ? selectedItemRef : null}
              className={selectedAreaId === area.id ? 'selected' : ''}
              onClick={() => handleAreaClick(area)}
            >
              <div
                style={{
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  paddingTop: '4px',
                }}
              >
                <Box sx={{ color: (theme) => theme.custom.primary }}>
                  <Areas />
                </Box>
              </div>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                {isEditMode && editingAreaId === area.id ? (
                  <TextField
                    value={editingAreaName || area.name}
                    onChange={(e) => onAreaNameChange?.(e.target.value)}
                    size="small"
                    variant="outlined"
                    fullWidth
                    autoFocus
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: (theme) => theme.custom.background1,
                        color: (theme) => theme.custom.text,
                        borderRadius: '20px',
                        '& fieldset': {
                          borderColor: (theme) => theme.custom.stroke,
                          borderRadius: '20px',
                        },
                        '&:hover fieldset': {
                          borderColor: (theme) => theme.custom.primary,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: (theme) => theme.custom.primary,
                        },
                        '& input': {
                          color: (theme) => theme.custom.text,
                        },
                      },
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <>
                    <AreaListItemText
                      primary={area.name}
                      secondary={formatAreaDescription(area)}
                    />
                    {renderAreaAssignments(area)}
                  </>
                )}
              </Box>

              {!isEditMode && onAreaAssignmentClick && (
                <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => handleAssignmentClick(e, area)}
                    sx={{
                      minWidth: 'auto',
                      padding: '4px 8px',
                      borderColor: (theme) => theme.custom.primary,
                      color: (theme) => theme.custom.primary,
                      '&:hover': {
                        borderColor: (theme) => theme.custom.primaryDark,
                        backgroundColor: (theme) => theme.custom.primaryLight,
                      },
                    }}
                  >
                    <PersonAdd sx={{ fontSize: 16 }} />
                  </Button>
                </Box>
              )}
            </AreaListItemContainer>
          ))
        )}
      </AreaList>
    </AreaListContainer>
  );
};

export default AreaListComponent;
